import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { collection, getDocs, query, where, addDoc, updateDoc, doc, orderBy } from 'firebase/firestore';
import { db } from '../firebase/config';
import { STORES, getAllFromCache, replaceStore } from '../utils/cache';
import logger from '../utils/logger';
import { handleError } from '../utils/errorHandler';
import toast from 'react-hot-toast';
import { isBusinessPubliclyVisible, hasBusinessPhoto } from '../services/listingFeeService';
import { getStoredAgentCode } from '../utils/agentAttribution';
import { stampRegistrationAgent } from '../services/commercialAgentService';

const BusinessContext = createContext();

export const useBusiness = () => {
  const context = useContext(BusinessContext);
  if (!context) {
    throw new Error('useBusiness must be used within a BusinessProvider');
  }
  return context;
};

// Liste complète des catégories prédéfinies
const ALL_CATEGORIES = [
  { id: 'restaurants', name: 'Restaurants', icon: '🍽️', short: 'Restaurants' },
  { id: 'hotels', name: 'Hôtels', icon: '🏨', short: 'Hôtels' },
  { id: 'pharmacies', name: 'Pharmacies', icon: '💊', short: 'Pharmacies' },
  { id: 'hopitaux', name: 'Hôpitaux', icon: '🏥', short: 'Hôpitaux' },
  { id: 'banques', name: 'Banques', icon: '🏦', short: 'Banques' },
  { id: 'ecoles', name: 'Écoles', icon: '🎓', short: 'Écoles' },
  { id: 'universites', name: 'Universités', icon: '🏛️', short: 'Universités' },
  { id: 'transport', name: 'Transport', icon: '🚌', short: 'Transport' },
  { id: 'shopping', name: 'Shopping', icon: '🛍️', short: 'Shopping' },
  { id: 'loisirs', name: 'Loisirs', icon: '🎮', short: 'Loisirs' },
  { id: 'sport', name: 'Sport', icon: '⚽', short: 'Sport' },
  { id: 'beaute', name: 'Beauté', icon: '💄', short: 'Beauté' },
  { id: 'automobile', name: 'Automobile', icon: '🚗', short: 'Automobile' },
  { id: 'logistique', name: 'Logistique', icon: '🚚', short: 'Logistique' },
  { id: 'agriculture', name: 'Agriculture', icon: '🌾', short: 'Agriculture' },
  { id: 'btp', name: 'BTP', icon: '🏗️', short: 'BTP' },
  { id: 'technologie', name: 'Technologie', icon: '💻', short: 'Technologie' },
  { id: 'administration', name: 'Administration', icon: '🏛️', short: 'Administration' },
  { id: 'services', name: 'Services', icon: '🔧', short: 'Services' },
  { id: 'autre', name: 'Autre', icon: '📋', short: 'Autre' }
];

export const BusinessProvider = ({ children }) => {
  const [approvedBusinesses, setApprovedBusinesses] = useState([]);
  const [pendingBusinesses, setPendingBusinesses] = useState([]);
  const [categories] = useState(ALL_CATEGORIES); // Utiliser la liste complète
  const [loading, setLoading] = useState(true);
  const [loadingPending, setLoadingPending] = useState(false);
  const [catalogFromCache, setCatalogFromCache] = useState(false);

  // Charger toutes les entreprises approuvées (y compris inactives — pour l'admin)
  const loadBusinessesFromFirestore = useCallback(async (silent = false) => {
    try {
      const businessesQuery = query(
        collection(db, 'businesses'),
        where('status', '==', 'approved')
      );
      const businessesSnapshot = await getDocs(businessesQuery);
      const businessesData = businessesSnapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data()
      }));

      setApprovedBusinesses(businessesData);

      const publicBusinesses = businessesData.filter((b) => isBusinessPubliclyVisible(b));
      await replaceStore(STORES.BUSINESSES, publicBusinesses, 30 * 60 * 1000);
      setCatalogFromCache(false);

      if (!silent) {
        logger.log('Businesses loaded from Firestore', {
          approved: businessesData.length,
          public: publicBusinesses.length
        });
      }
      return businessesData;
    } catch (error) {
      logger.error(error, {
        context: {
          action: 'loadBusinessesFromFirestore',
          code: error?.code,
          message: error?.message
        }
      });
      if (!silent) {
        throw error;
      }
      return null;
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, []);

  const loadBusinesses = useCallback(async () => {
    try {
      setLoading(true);

      const cachedBusinesses = await getAllFromCache(STORES.BUSINESSES);
      if (cachedBusinesses && cachedBusinesses.length > 0) {
        setApprovedBusinesses((prev) => (prev.length > 0 ? prev : cachedBusinesses));
        setCatalogFromCache(true);
        setLoading(false);
        logger.log('Businesses loaded from cache', { count: cachedBusinesses.length });
        if (typeof navigator !== 'undefined' && !navigator.onLine) {
          toast('Hors ligne : catalogue en cache', {
            id: 'businesses-offline-cache',
            icon: '📡',
            duration: 3500
          });
        }
        loadBusinessesFromFirestore(true);
        return;
      }

      await loadBusinessesFromFirestore(false);
    } catch (error) {
      logger.error(error, {
        context: {
          action: 'loadBusinesses',
          code: error?.code,
          message: error?.message
        }
      });
      // Ne jamais remplacer un catalogue déjà chargé par un tableau vide
      setLoading(false);
      toast.error(
        'Impossible de charger les entreprises. Vérifiez votre connexion et réessayez.',
        { id: 'businesses-load-error', duration: 5000 }
      );
    }
  }, [loadBusinessesFromFirestore]);

  useEffect(() => {
    loadBusinesses();
  }, [loadBusinesses]);

  // Charger les entreprises en attente (pour les admins)
  const loadPendingBusinesses = async () => {
    try {
      setLoadingPending(true);
      
      // Essayer avec orderBy d'abord
      try {
        const pendingQuery = query(
          collection(db, 'businesses'),
          where('status', '==', 'pending'),
          orderBy('createdAt', 'desc')
        );
        const pendingSnapshot = await getDocs(pendingQuery);
        const pendingData = pendingSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setPendingBusinesses(pendingData);
        logger.log('Pending businesses loaded from Firestore', { count: pendingData.length });
      } catch (error) {
        // Si l'index n'existe pas, charger toutes les entreprises et filtrer
        if (error.code === 'failed-precondition' || error.message?.includes('index')) {
          logger.warn('Index missing, loading all businesses and filtering', { error: error.message });
          const allSnapshot = await getDocs(collection(db, 'businesses'));
          const allBusinesses = [];
          allSnapshot.forEach((docSnap) => {
            const data = docSnap.data();
            if (data.status === 'pending') {
              allBusinesses.push({ id: docSnap.id, ...data });
            }
          });
          // Trier par date de création (plus récent en premier)
          allBusinesses.sort((a, b) => {
            const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
            const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
            return dateB - dateA;
          });
          setPendingBusinesses(allBusinesses);
          logger.log('Pending businesses loaded (fallback)', { count: allBusinesses.length });
        } else {
          throw error;
        }
      }
    } catch (error) {
      logger.error(error, { context: { action: 'loadPendingBusinesses' } });
      setPendingBusinesses([]);
    } finally {
      setLoadingPending(false);
    }
  };

  // Catalogue public (approuvées, actives, frais à jour)
  const validatedBusinesses = useMemo(() => {
    return approvedBusinesses.filter((b) => isBusinessPubliclyVisible(b));
  }, [approvedBusinesses]);

  // Alias historique — toujours le catalogue public
  const businesses = validatedBusinesses;

  // Calculer la note moyenne
  const getAverageRating = () => {
    if (validatedBusinesses.length === 0) return '0.0';
    
    const totalRating = validatedBusinesses.reduce((sum, business) => {
      const rating = business.rating || business.averageRating || 0;
      return sum + (typeof rating === 'number' ? rating : parseFloat(rating) || 0);
    }, 0);
    
    const average = totalRating / validatedBusinesses.length;
    return average.toFixed(1);
  };

  // Obtenir les entreprises par catégorie
  const getBusinessesByCategory = useCallback((categorySlug) => {
    if (!categorySlug) return [];
    
    return validatedBusinesses.filter(b => {
      // Normaliser le categorySlug pour la comparaison
      const normalizedSlug = categorySlug.toLowerCase().trim();
      
      // Essayer plusieurs formats de correspondance
      const businessCategory = b.categorySlug || b.category || b.categoryId;
      
      if (!businessCategory) return false;
      
      // Comparaison directe
      const normalizedBusinessCategory = typeof businessCategory === 'string' 
        ? businessCategory.toLowerCase().trim().replace(/\s+/g, '-')
        : String(businessCategory).toLowerCase().trim().replace(/\s+/g, '-');
      
      return normalizedBusinessCategory === normalizedSlug ||
             normalizedBusinessCategory === normalizedSlug.replace(/\s+/g, '-');
    });
  }, [validatedBusinesses]);

  // Soumettre une nouvelle entreprise
  const submitBusiness = async (businessData, userId = null) => {
    try {
      const agentCode = getStoredAgentCode() || undefined;
      const businessDoc = {
        ...businessData,
        userId: userId,
        status: 'pending',
        createdAt: new Date().toISOString(),
        submittedAt: new Date().toISOString(),
        ...(agentCode ? { agentCode } : {})
      };

      const docRef = await addDoc(collection(db, 'businesses'), businessDoc);

      if (agentCode) {
        await stampRegistrationAgent({
          targetCollection: 'businesses',
          targetId: docRef.id,
          agentCode
        });
      }
      
      logger.log('Business submitted successfully', { id: docRef.id, userId });
      
      return {
        success: true,
        id: docRef.id
      };
    } catch (error) {
      logger.error(error, { context: { action: 'submitBusiness', userId } });
      handleError(error, { showToast: true });
      return {
        success: false,
        error: error.message || 'Erreur lors de la soumission de l\'entreprise'
      };
    }
  };

  // Mettre à jour une entreprise existante
  const updateBusiness = async (businessId, updateData) => {
    try {
      const businessRef = doc(db, 'businesses', businessId);
      
      const updateDocData = {
        ...updateData,
        updatedAt: new Date().toISOString()
      };

      await updateDoc(businessRef, updateDocData);
      
      logger.log('Business updated successfully', { id: businessId });
      
      if (updateData.status === 'pending' || updateData.status === 'approved' || updateData.status === 'rejected') {
        await loadPendingBusinesses();
      }
      await loadBusinessesFromFirestore(true);
      
      return {
        success: true
      };
    } catch (error) {
      logger.error(error, { context: { action: 'updateBusiness', businessId } });
      handleError(error, { showToast: true });
      return {
        success: false,
        error: error.message || 'Erreur lors de la mise à jour de l\'entreprise'
      };
    }
  };

  // Approuver une entreprise (visible sauf si frais annuels expirés)
  const approveBusiness = async (businessId) => {
    try {
      const businessRef = doc(db, 'businesses', businessId);
      const existing = approvedBusinesses.find((b) => b.id === businessId)
        || pendingBusinesses.find((b) => b.id === businessId);
      let snapData = existing || null;
      if (!snapData) {
        const { getDoc } = await import('firebase/firestore');
        const snap = await getDoc(businessRef);
        if (snap.exists()) snapData = snap.data();
      }
      const isActive = isBusinessPubliclyVisible({
        ...(snapData || {}),
        status: 'approved',
        isActive: true
      });

      await updateDoc(businessRef, {
        status: 'approved',
        isActive: true,
        approvedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      
      logger.log('Business approved successfully', { id: businessId, isActive });
      
      await loadPendingBusinesses();
      await loadBusinessesFromFirestore(false);
      
      toast.success(
        isActive
          ? 'Entreprise approuvée et visible'
          : !hasBusinessPhoto({ ...(snapData || {}), status: 'approved' })
            ? 'Entreprise approuvée — ajoutez une photo pour l’affichage public'
            : 'Entreprise approuvée — paiement des frais d’affichage requis pour l’affichage public'
      );
      
      return { success: true, isActive };
    } catch (error) {
      logger.error(error, { context: { action: 'approveBusiness', businessId } });
      handleError(error, { showToast: true });
      return {
        success: false,
        error: error.message || 'Erreur lors de l\'approbation'
      };
    }
  };

  // Rejeter une entreprise
  const rejectBusiness = async (businessId) => {
    try {
      const businessRef = doc(db, 'businesses', businessId);
      await updateDoc(businessRef, {
        status: 'rejected',
        rejectedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      
      logger.log('Business rejected successfully', { id: businessId });
      
      await loadPendingBusinesses();
      await loadBusinessesFromFirestore(true);
      
      toast.success('Entreprise rejetée');
      
      return { success: true };
    } catch (error) {
      logger.error(error, { context: { action: 'rejectBusiness', businessId } });
      handleError(error, { showToast: true });
      return {
        success: false,
        error: error.message || 'Erreur lors du rejet'
      };
    }
  };

  // Basculer le statut actif/inactif d'une entreprise
  const toggleBusinessStatus = async (businessId) => {
    try {
      const business =
        approvedBusinesses.find((b) => b.id === businessId) ||
        validatedBusinesses.find((b) => b.id === businessId);

      if (!business) {
        throw new Error('Entreprise non trouvée');
      }

      // undefined / true = actuellement visible → on désactive
      const currentlyActive = business.isActive !== false;
      const nextActive = !currentlyActive;

      const businessRef = doc(db, 'businesses', businessId);
      await updateDoc(businessRef, {
        isActive: nextActive,
        updatedAt: new Date().toISOString()
      });

      setApprovedBusinesses((prev) =>
        prev.map((b) => (b.id === businessId ? { ...b, isActive: nextActive } : b))
      );

      logger.log('Business status toggled successfully', { id: businessId, isActive: nextActive });

      await loadBusinessesFromFirestore(true);

      return { success: true, isActive: nextActive };
    } catch (error) {
      logger.error(error, { context: { action: 'toggleBusinessStatus', businessId } });
      handleError(error, { showToast: true });
      return {
        success: false,
        error: error.message || 'Erreur lors de la modification du statut'
      };
    }
  };

  const value = {
    businesses,
    approvedBusinesses,
    pendingBusinesses,
    categories,
    validatedBusinesses,
    loading,
    loadingPending,
    loadBusinesses,
    getAverageRating,
    getBusinessesByCategory,
    submitBusiness,
    updateBusiness,
    approveBusiness,
    rejectBusiness,
    toggleBusinessStatus,
    loadPendingBusinesses,
    catalogFromCache
  };

  return (
    <BusinessContext.Provider value={value}>
      {children}
    </BusinessContext.Provider>
  );
};

